import { logger } from '@/shared/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

// Simple debounce utility
function debounce(fn, delay) {
   let timer = null;
   return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
   };
}

export function useBidirectionalInfiniteScroll({
   containerRef,
   onLoadTop = null,
   onLoadBottom = null,
   hasMoreTop = false,
   hasMoreBottom = false,
   offset = 3, // Default 3rem
   debounceMs = 300,
   inverted = false,
}) {
   const [loadingTop, setLoadingTop] = useState(false);
   const [loadingBottom, setLoadingBottom] = useState(false);

   const loadingTopRef = useRef(false);
   const loadingBottomRef = useRef(false);
   const lastTopTriggerRef = useRef(0);
   const lastBottomTriggerRef = useRef(0);
   const COOLDOWN_MS = 300;

   // Use refs for stable access to handlers and state flags in handleScroll
   const onLoadTopRef = useRef(onLoadTop);
   const onLoadBottomRef = useRef(onLoadBottom);
   const hasMoreTopRef = useRef(hasMoreTop);
   const hasMoreBottomRef = useRef(hasMoreBottom);
   const invertedRef = useRef(inverted);

   useEffect(() => {
      onLoadTopRef.current = onLoadTop;
      onLoadBottomRef.current = onLoadBottom;
      hasMoreTopRef.current = hasMoreTop;
      hasMoreBottomRef.current = hasMoreBottom;
      invertedRef.current = inverted;
   }, [onLoadTop, onLoadBottom, hasMoreTop, hasMoreBottom, inverted]);

   // Keep loading refs in sync
   useEffect(() => {
      loadingTopRef.current = loadingTop;
   }, [loadingTop]);
   useEffect(() => {
      loadingBottomRef.current = loadingBottom;
   }, [loadingBottom]);

   // The loading state is managed within handleScroll's promise lifecycle.
   // We don't need a separate effect to sync it with hasMoreBottom toggles
   // because the finally block in handleScroll will always clear it.

   const handleScroll = useCallback(() => {
      const el = containerRef.current;
      if (!el) return;

      if (el.scrollHeight <= el.clientHeight) return;

      const allowTop = hasMoreTopRef.current && typeof onLoadTopRef.current === 'function';
      const allowBottom = hasMoreBottomRef.current && typeof onLoadBottomRef.current === 'function';

      if (!allowTop && !allowBottom) return;

      // Handle scroll position calculations based on list direction
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      const maxScroll = scrollHeight - clientHeight;

      let distanceFromTop, distanceFromBottom;

      if (invertedRef.current) {
         /**
          * Column-reverse list: scrollTop=0 is visual BOTTOM (newest), scrollTop=-maxScroll is visual TOP (oldest).
          *
          * We want:
          *   - "bottom trigger" (onLoadBottom = loadOlderMessages) to fire at visual TOP (oldest end)
          *   - "top trigger"    (onLoadTop    = loadNewerMessages)  to fire at visual BOTTOM (newest end)
          *
          * So distanceFromBottom must be near 0 at visual TOP, and distanceFromTop near 0 at visual BOTTOM.
          */
         distanceFromBottom = Math.abs(maxScroll + scrollTop); // → 0 at visual TOP (scrollTop = -maxScroll)
         distanceFromTop = Math.abs(scrollTop);                // → 0 at visual BOTTOM (scrollTop = 0)
      } else {
         distanceFromTop = Math.max(0, scrollTop);
         distanceFromBottom = Math.max(0, scrollHeight - scrollTop - clientHeight);
      }

      // Convert current offset (rem) to pixels based on root font size
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const offsetPx = offset * rootFontSize;


      // 🔹 TOP trigger - check if we're near the top
      const topTrigger = allowTop && distanceFromTop <= offsetPx;

      if (topTrigger && !loadingTopRef.current) {
         const now = Date.now();
         if (now - lastTopTriggerRef.current >= COOLDOWN_MS) {
            lastTopTriggerRef.current = now;
            loadingTopRef.current = true;
            setLoadingTop(true);
            Promise.resolve(onLoadTopRef.current?.())
               .catch(error => {
                  logger.error('❌ onLoadTop failed:', error);
               })
               .finally(() => {
                  setLoadingTop(false);
                  loadingTopRef.current = false;
               });
         } else {
            // cooldown active, skip
         }
      }

      // 🔹 BOTTOM trigger - check if we're near the bottom
      const bottomTrigger = allowBottom && distanceFromBottom <= offsetPx;

      if (bottomTrigger && !loadingBottomRef.current) {
         const now = Date.now();
         if (now - lastBottomTriggerRef.current >= COOLDOWN_MS) {
            lastBottomTriggerRef.current = now;
            loadingBottomRef.current = true;
            setLoadingBottom(true);

            setTimeout(() => {
               Promise.resolve(onLoadBottomRef.current?.())
                  .catch(error => {
                     logger.error('❌ onLoadBottom failed:', error);
                  })
                  .finally(() => {
                     setLoadingBottom(false);
                     loadingBottomRef.current = false;
                  });
            }, 100); // 100ms delay to allow layout to settle
         } else {
            // cooldown active, skip
         }
      }
   }, [containerRef, offset]);

   // Attach debounced scroll listener
   useEffect(() => {
      const el = containerRef.current;
      if (!el) return;

      const debounced = debounce(handleScroll, debounceMs);
      el.addEventListener('scroll', debounced);
      // Trigger once on mount to catch already-scrolled containers
      requestAnimationFrame(() => {
         handleScroll();
      });

      return () => {
         el.removeEventListener('scroll', debounced);
      };
   }, [handleScroll, debounceMs, containerRef]);

   return { loadingTop, loadingBottom };
}
